/**
 * The staff-fill hand-off: the practice dashboard embeds this webapp in a frame and posts the session token
 * into it. Everything the frame receives is checked against the dashboard origin named in the frame URL, and that
 * origin is the only target the frame ever posts to.
 */
export const STAFF_FILL_MESSAGE = {
  ready: "pf-staff-fill:ready",
  session: "pf-staff-fill:session",
  done: "pf-staff-fill:done",
  end: "pf-staff-fill:end",
} as const;

type StaffFillSession = {
  token: string;
  practiceId: string;
  chainId: string;
  practicePatientId: string;
};

// Every domain practice users reach the dashboard under (the unused default *.web.app hosts of production stay out),
// kept identical to the frame-ancestors lists in firebase.json: the CSP decides whether the frame renders at all, this
// list whether it answers.
const DASHBOARD_ORIGINS: Record<string, string[]> = {
  production: [
    "https://app.physiofit.app",
    "https://portal.physiofit.app",
    "https://www.portal.physiofit.app",
    "https://beta.physiofit.app",
  ],
  development: [
    "https://io-physiofit-development.web.app",
    "https://partner-physiofit-dev.web.app",
    "http://localhost:5173",
    "http://localhost:5174",
    "http://localhost:5176",
  ],
};

export const readDashboardOrigin = (search: URLSearchParams): string | undefined => {
  const origin = search.get("origin") ?? "";
  const allowed = DASHBOARD_ORIGINS[import.meta.env.VITE_ENV ?? "development"] ?? [];
  return allowed.includes(origin) ? origin : undefined;
};

export const isFramed = () => window.parent !== window;

export const postToDashboard = (message: { type: string; [key: string]: unknown }, origin: string) =>
  window.parent.postMessage(message, origin);

export const readSessionMessage = (data: unknown): StaffFillSession | undefined => {
  const message = data as (Partial<StaffFillSession> & { type?: unknown }) | null;
  const filled = (value: unknown): value is string => typeof value === "string" && value !== "";
  if (
    message?.type !== STAFF_FILL_MESSAGE.session ||
    !filled(message.token) ||
    !filled(message.practiceId) ||
    !filled(message.chainId) ||
    !filled(message.practicePatientId)
  ) {
    return undefined;
  }
  return {
    token: message.token,
    practiceId: message.practiceId,
    chainId: message.chainId,
    practicePatientId: message.practicePatientId,
  };
};
